import { Component, h, Host, Prop, Method, Element, Build, Watch } from '@stencil/core';

const COMPONENT_NAME = 'ul-popup-form-loader';

/**
 * Window event that opens the overlay. Dispatched by the `ui:ul-open-editor` action right before it
 * redirects to the editor, which hardcodes the same name — the two must stay in sync. Kept as a
 * local constant rather than imported because the action does not export it (an action may not be
 * reached into by a component for a value, and the string is the whole contract).
 */
const LOADER_OPEN_EVENT = 'ul-form-loader-open';

/**
 * Helper overlay shown while the visitor waits for the USLegal form editor to open: a dimming veil
 * with a spinner and a status label, centred over the viewport.
 * Not an authored block — it carries no content, is used inside the form flow, and opens on the
 * `ul-form-loader-open` window event the `ui:ul-open-editor` action dispatches before it navigates.
 * It only reports the wait; the request and redirect belong to that action.
 *
 * @product uslegal
 * @version 1.0.0
 * @category USLegal
 * @contentConfig Internal loading overlay shown after a visitor clicks a "Get form" CTA
 *
 * @example
 *   <ul-popup-form-loader loading-text="Loading"></ul-popup-form-loader>
 */
@Component({
  tag: 'ul-popup-form-loader',
  styleUrl: 'index.scss',
  shadow: false,
})
export class UlPopupFormLoader {
  @Element() host: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'uslegal' | 'inverted' = 'uslegal';

  /**
   * Status label shown under the spinner.
   * @content
   * @default Loading
   */
  @Prop({ attribute: 'loading-text' }) loadingText: string = 'Loading';

  /**
   * Controls loader visibility.
   * @default false
   */
  @Prop({ mutable: true, reflect: true }) isOpen: boolean = false;

  /**
   * Covers `is-open` written from outside (the prop is reflected, so an attribute write is a valid
   * way in) and every close — locking or unlocking the body scroll to match.
   */
  @Watch('isOpen')
  watchIsOpen(newValue: boolean) {
    if (!Build.isBrowser) return;
    if (newValue) this.lockBody();
    else this.unlockBody();
  }

  connectedCallback() {
    if (!Build.isBrowser) return;
    window.addEventListener(LOADER_OPEN_EVENT, this.handleOpenEvent);
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;
    // `isOpen` can already be true when the element upgrades (an `is-open` attribute written before
    // hydration), which never ran through the watcher — lock the body here for that case.
    if (this.isOpen) this.lockBody();
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    window.removeEventListener(LOADER_OPEN_EVENT, this.handleOpenEvent);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.unlockBody();
  }

  private handleOpenEvent = () => {
    this.isOpen = true;
  };

  /**
   * Safari restores a page from the back/forward cache instead of reloading it, so returning from
   * the editor would replay the DOM with the overlay still up and the body still locked. `pageshow`
   * with `persisted` is the one signal a bfcache restore emits, so the loader resets there.
   */
  private handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) this.isOpen = false;
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setTimeout(() => window.location.reload(), 200);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  private lockBody() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add(`${COMPONENT_NAME}-open`);
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  private unlockBody() {
    document.body.classList.remove(`${COMPONENT_NAME}-open`);
    document.body.style.paddingRight = '';
  }

  /** Opens the loader overlay. */
  @Method()
  async open() {
    this.isOpen = true;
  }

  /** Closes the loader overlay. */
  @Method()
  async close() {
    this.isOpen = false;
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <div
          class={{
            [COMPONENT_NAME]: true,
            [`${COMPONENT_NAME}--visible`]: this.isOpen,
          }}
          role="dialog"
          aria-modal="true"
          aria-label={this.loadingText}
        >
          <div class={`${COMPONENT_NAME}__body`}>
            <span class={`${COMPONENT_NAME}__spinner`} aria-hidden="true"></span>
            <p class={`${COMPONENT_NAME}__label`}>{this.loadingText}</p>
          </div>
        </div>
      </Host>
    );
  }
}
